<script setup lang="ts">
  import { VDivider, VRadio } from "@krainovsd/vue-ui";
  import { computed, ref, watch } from "vue";
  import { useI18n } from "vue-i18n";
  import { isReportSubstringDisabled } from "@/entities/report";
  import { GROUPS_BY_OPTIONS, SECTION_OPTIONS } from "@/entities/report/report.constants";
  import Card from "@/components/atoms/Card.vue";
  import StepLayout from "@/components/molecules/constructor/StepLayout.vue";

  const { t } = useI18n();

  const entityKey = defineModel<number>("entity", { required: true });
  const groupByKey = defineModel<number | null>("groupBy", { required: true });
  const emit = defineEmits<{ next: []; back: [] }>();

  const layoutRef = ref<{ rootEl: HTMLElement | null } | null>(null);
  defineExpose({
    rootEl: computed(() => layoutRef.value?.rootEl ?? null),
  });

  const entitiesOptions = computed(() =>
    SECTION_OPTIONS.map((option) => ({
      ...option,
      title: t(option.title.path, {}, { default: option.title.default }),
      description: t(option.description.path, {}, { default: option.description.default }),
    })),
  );

  const groupByOptions = computed(() =>
    GROUPS_BY_OPTIONS.map((option) => ({
      ...option,
      title: t(option.title.path, {}, { default: option.title.default }),
    })),
  );

  // A group-by ("Разбивка по") option is disabled when the chosen entity
  // (section) forbids it (date hierarchy / triggers) or when it repeats the
  // section. "Без разбивки" (null) is always allowed.
  function isGroupByDisabled(key: number | null): boolean {
    if (key === null) return false;
    return entityKey.value === key || isReportSubstringDisabled(entityKey.value, key);
  }

  function selectGroupBy(key: number | null) {
    if (isGroupByDisabled(key)) return;
    groupByKey.value = key;
  }

  // Changing the entity can invalidate the current group-by — drop it to null.
  watch(entityKey, () => {
    if (groupByKey.value !== null && isGroupByDisabled(groupByKey.value)) {
      groupByKey.value = null;
    }
  });
</script>

<template>
  <StepLayout
    ref="layoutRef"
    :title="$t('page.createReport.step4.title', {}, { default: 'Выберите разрез' })"
    :subtitle="
      $t(
        'page.createReport.step4.subtitle',
        {},
        { default: 'По какой сущности группируем строки отчёта' },
      )
    "
    back
    next
    @back="emit('back')"
    @next="emit('next')"
  >
    <div :class="$style.entities">
      <Card
        v-for="entity in entitiesOptions"
        :key="entity.key"
        :class="$style.entityCard"
        @click="entityKey = entity.key"
      >
        <VRadio
          v-model="entityKey"
          :name="entity.title"
          :value="entity.key"
          :class="$style.entityRadio"
        />
        <div :class="$style.entityContent">
          <span :class="$style.entityTitle">{{ entity.title }}</span>
          <div :class="$style.entityDescription">{{ entity.description }}</div>
        </div>
      </Card>
    </div>
    <VDivider type="horizontal" />
    <div :class="$style.groupBy">
      <Card
        v-for="groupBy in groupByOptions"
        :key="String(groupBy.key)"
        :class="[$style.groupByCard, isGroupByDisabled(groupBy.key) && $style.groupByCardDisabled]"
        @click="selectGroupBy(groupBy.key)"
      >
        <VRadio
          :model-value="groupByKey as number"
          :name="groupBy.title"
          :value="groupBy.key as number"
          :class="$style.groupByRadio"
        />
        <span :class="$style.entityTitle">{{ groupBy.title }}</span>
      </Card>
    </div>
  </StepLayout>
</template>

<style lang="scss" module>
  .entities {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .entityCard {
    padding: 14px;
    display: flex;
    align-items: flex-start;

    cursor: pointer;
  }

  .entityContent {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .entityRadio {
    margin-top: 4px;
  }

  .entityTitle {
    color: var(--foreground);
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .entityDescription {
    color: var(--muted-foreground);
    font-size: 13px;
    line-height: 1.375;
  }

  .groupBy {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .groupByCard {
    padding: 0.5rem 0.75rem;

    display: flex;
    align-items: flex-start;
    gap: 0.5rem;

    cursor: pointer;
  }

  .groupByCardDisabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  .groupByRadio {
    :global(.ksd-radio__text) {
      padding-inline: 0;
    }
  }
</style>
